import type { TRTCConnection } from 'webrtc/types';

/**
 * Pure, state-free helpers that turn the raw remote list into the customer-safe render list —
 * extracted from the hook so the render rules stay unit-testable in isolation.
 */

/** A "live" stream is a `MediaStream`; null / undefined / non-MediaStream is no stream. */
const hasLiveStream = (remote: TRTCConnection): boolean => remote.stream instanceof MediaStream;

/** The stable dealer identity for a remote, or `undefined` for a customer (no pseudonym). */
export const dealerPseudonymOf = (remote: Pick<TRTCConnection, 'meta'>): string | undefined => {
  const value = remote.meta?.dealerPseudonym;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

/**
 * The per-session digest of a remote's stable per-tab id, or `undefined` for a peer on an older
 * build. The server never broadcasts the raw id — it is the selector its supersede matches on —
 * and equality of the digest is all the dedup below needs.
 */
export const clientIdHashOf = (remote: Pick<TRTCConnection, 'meta'>): string | undefined => {
  const value = remote.meta?.clientIdHash;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

/**
 * AVS-4302: a "peer connected/disconnected" toast should announce only a genuinely different
 * party — the customer, or a DIFFERENT dealer. A same-dealer connection is THIS dealer's own
 * other tab (a second tab, or a takeover reconnect) — i.e. "me", not a peer — so its toast is
 * suppressed. Customers (no pseudonym) and different dealers are always announced; the check is
 * identity-scoped via the dealer pseudonym and applies only to a dealer viewer.
 */
export const isOwnDealerPeer = (
  peer: Pick<TRTCConnection, 'meta'> | undefined,
  myPseudonym: string | undefined,
  isDealer: boolean
): boolean => {
  if (!isDealer || !myPseudonym) return false;
  return dealerPseudonymOf(peer ?? {}) === myPseudonym;
};

/**
 * §5.6 — collapse remotes sharing the same `meta.dealerPseudonym` to ONE tile so the
 * customer never sees one dealer twice (takeover overlap, claim race, unmanaged
 * second-dealer duplicate, or a stale election-unaware client). Customers (no
 * pseudonym) are NEVER collapsed — each stays distinct. Distinct dealers stay distinct.
 *
 * Per-group winner, in order: (1) the active speaker, else (2) one with a live stream
 * (publishing master beats a no-stream standby), else (3) the first in input order
 * (stable). Output preserves the input order of the surviving remotes.
 */
export const dedupeRemotesByDealer = (remotes: TRTCConnection[]): TRTCConnection[] => {
  // Pick the winning connectionId per dealer first, then emit survivors in input order
  // so the result stays stable (no reordering of customers or distinct dealers).
  const winnerByDealer = new Map<string, TRTCConnection>();
  for (const remote of remotes) {
    const dealer = dealerPseudonymOf(remote);
    if (dealer === undefined) continue;
    const current = winnerByDealer.get(dealer);
    if (!current || beats(remote, current)) {
      winnerByDealer.set(dealer, remote);
    }
  }

  return remotes.filter((remote) => {
    const dealer = dealerPseudonymOf(remote);
    if (dealer === undefined) return true; // customers always survive
    return winnerByDealer.get(dealer) === remote;
  });
};

/**
 * AVS-4425 — collapse remotes sharing the same `meta.clientIdHash` to ONE tile.
 *
 * Two remotes with one hash are the same BROWSER TAB seen twice: a rejoin whose predecessor has
 * not been retired yet (the reported "if they rejoin multiple black thumbnails appear"). The
 * server now supersedes the old row at `$connect` and broadcasts its `ConnectionRemoved`, so this
 * is the render-side backstop for the window where that announcement is in flight, lost, or
 * came from a peer whose row was pruned by a path that could not announce.
 *
 * Distinct from {@link dedupeRemotesByDealer} in the two ways that matter: it is per-TAB rather
 * than per-human (a dealer's second tab keeps its own id and is never collapsed into the first),
 * and it applies to CUSTOMERS too — a customer has no pseudonym, so dealer dedup never protected
 * it. Remotes with no hash (older peers) are never collapsed. Output preserves the input order of
 * the surviving remotes.
 *
 * The per-group winner is the NEWEST entry, and deliberately NOT the dealer dedup's {@link beats}.
 * A tab group only ever has more than one member because that tab rejoined, so the later entry is
 * by construction the newer socket — and the whole reason this backstop runs is that the old row's
 * `ConnectionRemoved` was late or lost. The dealer rule inverts exactly that case: on a rejoin the
 * OLD entry still holds its frozen `MediaStream` and was inserted first, while the new one has no
 * stream for the first second or two, so "a live stream wins, ties keep first-seen" would pin the
 * dead tile and hide the live one — worse than the two tiles it replaced. Transport liveness is no
 * better a signal, because a dropped socket's `RTCPeerConnection` stays `connected` for tens of
 * seconds while its replacement is still `checking`. Peers that are genuinely unusable are already
 * gone: {@link filterDeadTransportTiles} and {@link filterIdentityPendingTiles} both run first.
 */
export const dedupeRemotesByClient = (remotes: TRTCConnection[]): TRTCConnection[] => {
  // Last write wins — see above.
  const winnerByClient = new Map<string, TRTCConnection>();
  for (const remote of remotes) {
    const clientIdHash = clientIdHashOf(remote);
    if (clientIdHash !== undefined) winnerByClient.set(clientIdHash, remote);
  }

  return remotes.filter((remote) => {
    const clientIdHash = clientIdHashOf(remote);
    if (clientIdHash === undefined) return true; // unidentified peers always survive
    return winnerByClient.get(clientIdHash) === remote;
  });
};

/** True when `candidate` should replace the `current` winner for the same dealer group. */
const beats = (candidate: TRTCConnection, current: TRTCConnection): boolean => {
  // (1) active speaker wins outright.
  if (!!candidate.isActiveSpeaker !== !!current.isActiveSpeaker) {
    return !!candidate.isActiveSpeaker;
  }
  if (candidate.isActiveSpeaker) return false; // both active — keep the first seen (stable)
  // (2) a live stream wins over a no-stream sibling.
  if (hasLiveStream(candidate) !== hasLiveStream(current)) {
    return hasLiveStream(candidate);
  }
  // (3) otherwise keep the first seen (stable) — candidate does not beat current.
  return false;
};

/*
 * Presence = tile: there is deliberately no "drop streamless remotes" helper here any more —
 * the old one made a camera-denied (receive-only) dealer invisible to the customer. Render
 * exclusions are identity-based or transport-based, never stream-based: same-dealer dedup
 * (above), the own-tab drop in `useVoipConnections`, {@link filterIdentityPendingTiles}, and
 * {@link filterDeadTransportTiles}. Everything else that is CONNECTED renders, with a
 * placeholder when it has no video.
 */

/**
 * AVS-4392 — drop peers whose transport the recovery ladder has given up on.
 *
 * This is emphatically NOT the stream-based exclusion AVS-4363 removed, and the distinction is
 * the whole point: a camera-denied peer is PRESENT but silent — it has a live transport, it just
 * publishes nothing — so it keeps its tile and renders a placeholder. A peer flagged
 * `transportFailed` has no working transport at all: its `RTCPeerConnection` is dead and cannot
 * deliver a frame by definition, so its tile could only ever be a gray rectangle (the reported
 * bug — a permanent gray box whose mute badges kept updating, because badges ride signaling).
 *
 * Render-only, like the dedup above: the publish/negotiate gate keys on the RAW peer count taken
 * before any of these filters, so excluding a dead peer here never suppresses an offer.
 */
export const filterDeadTransportTiles = (remotes: TRTCConnection[]): TRTCConnection[] =>
  remotes.filter((remote) => !remote.transportFailed);

/**
 * AVS-4302 takeover reliability — drop peers admitted from an inbound offer whose
 * `ConnectionAdded` (identity) has not arrived yet. They are fully negotiable, but without
 * `meta` every classifier above reads them as a customer: they would bypass the dealer dedup,
 * the own-tab exclusion, and could even win the active slot — a phantom tile. Render them only
 * once the Add merges their meta in. Non-pending customers (no meta, no flag) are untouched.
 */
export const filterIdentityPendingTiles = (remotes: TRTCConnection[]): TRTCConnection[] =>
  remotes.filter((remote) => !remote.metaPending);

export type TVoipHiddenReason =
  | 'dead-transport'
  | 'identity-pending'
  | 'self-mirror'
  /**
   * A MASTER's own OTHER tab (stage 4a below), via `myPseudonym` — deliberately distinct from
   * `'self-mirror'` (final review R1). A standby's own loopback and a master's own second tab point
   * in OPPOSITE directions in a takeover investigation: the former is expected and healthy, the
   * latter is the exact shape of the AVS-4423 own-tab-misattribution bug this module's whole stage
   * ordering exists to get right. Before this fix round both pushed `'self-mirror'`.
   */
  | 'own-tab'
  | 'dedupe'
  | 'suppressed'
  | 'tile-cap'
  /**
   * Final review R2, renamed from `'single-tile'`. Still means "no video tile", never "no audio" —
   * `RemoteAudioSinks` (root-level, decoupled from the tile list since AVS-4397) routes this
   * remote's audio regardless of which tile list `describeRenderList` computed. See the same note
   * on {@link IVoipRenderList.hidden} below.
   */
  | 'no-tile'
  | 'unexplained';

export type IVoipRenderList = {
  rendered: string[];
  /**
   * "Hidden" means no VIDEO TILE, never "no audio" (final review R2). `RemoteAudioSinks` renders at
   * the root, decoupled from the tile list since AVS-4397, and routes every remote's audio
   * regardless of `'tile-cap'`/`'no-tile'`/any other reason here — a remote in this array can be
   * fully audible.
   */
  hidden: Array<{ id: string; reason: TVoipHiddenReason }>;
};

/**
 * AVS-4423 Task 1.8 — the Session Flight Recorder's missing half of the VOIP render decision.
 *
 * `useVoipConnections` computes its render list by running several filters and dedupe passes, IN
 * THIS ORDER (`useVoipConnections.ts`):
 *
 * 1. drop the standby's own loopback (`selfMirrorConnectionId`), unconditionally;
 * 2. if incoming media is suppressed (`suppressIncoming`), stop — nothing else renders;
 * 3. `filterIdentityPendingTiles`, then `filterDeadTransportTiles`, then `dedupeRemotesByClient`
 *    (all unconditional);
 * 4. if the single-stream flag is on: drop every remaining remote sharing `myPseudonym` (a
 *    dealer's own OTHER tabs), THEN `dedupeRemotesByDealer`.
 *
 * This function reproduces that exact sequence — calling the SAME exported filter/dedupe
 * functions rather than re-deriving "why" from field values, and applying the own-tab exclusion
 * BEFORE the dealer dedupe rather than after, so a dealer's own second tab is never misattributed
 * as a `'dedupe'` casualty of some OTHER dealer's tile. Fix round 1 caught exactly that ordering
 * bug: master dealer-A with two of its own tabs plus a customer, single-stream on — the hook drops
 * BOTH A-tabs as `own-tab` exclusions (step 4, before any dedupe runs — final review R1 renamed
 * this reason from `self-mirror`, which this same doc comment used to call it), but a dealer-dedupe
 * run over the un-filtered list picks one A-tab as a false "winner" and reports the other as a
 * duplicate. See `voipRemotes.renderList.test.ts`'s "own-tab exclusion, not a dealer duplicate"
 * test.
 *
 * `myPseudonym` and `selfMirrorConnectionId` are the two identities `useVoipConnections.ts` has
 * that this module does not derive on its own — supplied by the caller so the own-tab/self-mirror
 * exclusions are DERIVED, not guessed. `suppressIncoming`, supplied likewise, gives a fully-
 * suppressed standby's peers their own `'suppressed'` reason instead of falling through the dedupe
 * stages and coming out mislabeled `'dedupe'` (fix round 1's second finding — every one of those
 * peers used to report as SOME kind of collapse, when the true cause is a standby that has not
 * taken over, not a duplicate of anything).
 *
 * Where this module's own reproduction of a CONDITIONAL stage (the own-tab filter and
 * `dedupeRemotesByDealer`, both single-stream-flag-gated in the hook) would disagree with the
 * ground truth `rendered` list actually supplied, ground truth always wins — see the "flag off"
 * test in `voipRemotes.renderList.test.ts`. Anything that survives every stage above yet is still
 * absent from ground truth reports `'unexplained'` — a hook behaviour this module does not yet
 * model, not a guess at one (fix round 1: a residual that quietly reused `'self-mirror'` for this
 * case sent a genuine customer's disappearance down the wrong investigation path).
 *
 * `remoteSlots`, when supplied, applies the tile cap on top of ground truth: only the first
 * `remoteSlots` entries of `rendered`, IN THE ORDER SUPPLIED, stay rendered — everything past that
 * index reports `'tile-cap'`. This IS `MultiVoipInternal.tsx`'s own `sortedRemotes.slice(0,
 * remoteSlots)`, performed here rather than by the caller: that component calls this function a
 * SECOND time (the hook itself has no `remoteSlots` — it is a component prop,
 * `maxParticipants`/the phone auto-cap), with `raw: sortedRemotes, rendered: sortedRemotes` (the
 * FULL identity-resolved list, NOT its own already-capped `visibleRemotes` — passing the
 * pre-sliced list here would double-apply the cap and everything past it would come out
 * `'unexplained'` instead of `'tile-cap'`, since it is no longer explained by ANY stage above) and
 * `remoteSlots` doing 100% of the slicing. See the doc comment on the effect in
 * `MultiVoipInternal.tsx` for why this is two recorded observations, not one.
 *
 * `singleTileConnectionId`, when supplied, is `SingularVoip.tsx`'s third call: that surface shows
 * exactly ONE tile — `remoteConnections[activeConnectionId]`, not `sortedRemotes[0]` —
 * `resolveActiveConnectionId` has its own sticky-previous/lowest-connectionId tie-breaks that do
 * not always agree with `sortedRemotes`'s speaker-rank sort, so this cannot be expressed as an
 * index cutoff the way `remoteSlots` is. Every OTHER entry in `rendered` reports `'no-tile'` (final
 * review R2 renamed this from `'single-tile'` — it still means no video tile, never no audio; see
 * {@link IVoipRenderList.hidden}). Mutually exclusive with `remoteSlots` in practice (no caller supplies both — `MultiVoipInternal`
 * and `SingularVoip` are different surfaces), but structurally independent here: with both
 * supplied, the index cutoff is evaluated first.
 */
export function describeRenderList(input: {
  raw: TRTCConnection[];
  rendered: TRTCConnection[];
  myPseudonym?: string;
  selfMirrorConnectionId?: string;
  suppressIncoming?: boolean;
  remoteSlots?: number;
  singleTileConnectionId?: string;
}): IVoipRenderList {
  const { raw, rendered, myPseudonym, selfMirrorConnectionId, suppressIncoming, remoteSlots, singleTileConnectionId } =
    input;
  const renderedIds = new Set(rendered.map((remote) => remote.connectionId));
  const hidden: IVoipRenderList['hidden'] = [];

  // Stage 1 (useVoipConnections.ts: `withoutSelfMirror`) — unconditional, runs before anything else.
  const withoutSelfMirror = raw.filter((remote) => {
    const isSelfMirror = selfMirrorConnectionId !== undefined && remote.connectionId === selfMirrorConnectionId;
    if (isSelfMirror && !renderedIds.has(remote.connectionId)) {
      hidden.push({ id: remote.connectionId, reason: 'self-mirror' });
    }
    return !isSelfMirror;
  });

  // Stage 2 (useVoipConnections.ts: the `suppressIncoming` early return) — nothing past this point
  // ever renders, so none of the later stages run at all: a suppressed peer is `'suppressed'`,
  // never `'dedupe'` or `'identity-pending'`, however it would otherwise have been classified.
  if (suppressIncoming) {
    for (const remote of withoutSelfMirror) {
      if (!renderedIds.has(remote.connectionId)) {
        hidden.push({ id: remote.connectionId, reason: 'suppressed' });
      }
    }
  } else {
    // Stage 3 — identity-pending, then dead-transport, then per-tab dedupe. Unconditional, exactly
    // like the hook.
    const afterIdentityPending = filterIdentityPendingTiles(withoutSelfMirror);
    tagDropped(withoutSelfMirror, afterIdentityPending, renderedIds, 'identity-pending', hidden);

    const afterDeadTransport = filterDeadTransportTiles(afterIdentityPending);
    tagDropped(afterIdentityPending, afterDeadTransport, renderedIds, 'dead-transport', hidden);

    const afterClientDedupe = dedupeRemotesByClient(afterDeadTransport);
    tagDropped(afterDeadTransport, afterClientDedupe, renderedIds, 'dedupe', hidden);

    // Stage 4a — a dealer's own OTHER tabs, dropped BEFORE the dealer dedupe runs (the ordering fix
    // round 1's own-tab-misattribution finding required): `!!myPseudonym` mirrors the hook's own
    // truthiness check (an empty pseudonym, i.e. a customer, never matches anything).
    const afterOwnTabs = afterClientDedupe.filter((remote) => {
      const isOwnTab = !!myPseudonym && dealerPseudonymOf(remote) === myPseudonym;
      if (isOwnTab && !renderedIds.has(remote.connectionId)) {
        hidden.push({ id: remote.connectionId, reason: 'own-tab' });
      }
      return !isOwnTab;
    });

    // Stage 4b — per-dealer dedupe, now over a list that no longer contains the viewer's own tabs.
    const afterDealerDedupe = dedupeRemotesByDealer(afterOwnTabs);
    tagDropped(afterOwnTabs, afterDealerDedupe, renderedIds, 'dedupe', hidden);

    // The residual: survived every stage above, yet still absent from ground truth. Every exclusion
    // this module knows about has already been applied by this point.
    for (const remote of afterDealerDedupe) {
      if (!renderedIds.has(remote.connectionId)) {
        hidden.push({ id: remote.connectionId, reason: 'unexplained' });
      }
    }
  }

  // The tile cap and/or the single-tile pick, applied on top of ground truth, in the exact order
  // `rendered` was supplied in (the caller passes `sortedRemotes` order — speaker-rank first —
  // matching `.slice(0, remoteSlots)`). See the doc comment above for why these are two independent
  // checks rather than one: no caller supplies both, but nothing here assumes that.
  const renderedOut: string[] = [];
  rendered.forEach((remote, index) => {
    if (remoteSlots !== undefined && index >= remoteSlots) {
      hidden.push({ id: remote.connectionId, reason: 'tile-cap' });
      return;
    }
    if (singleTileConnectionId !== undefined && remote.connectionId !== singleTileConnectionId) {
      hidden.push({ id: remote.connectionId, reason: 'no-tile' });
      return;
    }
    renderedOut.push(remote.connectionId);
  });

  return { rendered: renderedOut, hidden };
}

/**
 * Push a `{ id, reason }` for every connection `before` carries that `after` no longer does —
 * UNLESS the hook's own ground-truth `rendered` list says it actually survived (the conditional
 * stages, e.g. `dedupeRemotesByDealer` with the single-stream flag off, can disagree with this
 * module's unconditional reproduction; ground truth wins — see the doc comment above).
 */
function tagDropped(
  before: TRTCConnection[],
  after: TRTCConnection[],
  renderedIds: ReadonlySet<string>,
  reason: TVoipHiddenReason,
  hidden: IVoipRenderList['hidden']
): void {
  const survivorIds = new Set(after.map((remote) => remote.connectionId));
  for (const remote of before) {
    if (!survivorIds.has(remote.connectionId) && !renderedIds.has(remote.connectionId)) {
      hidden.push({ id: remote.connectionId, reason });
    }
  }
}

/**
 * AVS-4311 — choose which single remote the minimized pill's "Them" tile promotes (also the
 * expanded panel's `activeConnectionId`). It must be the last-known speaker and, when no one
 * has spoken yet, a DETERMINISTIC pick — never the arbitrary object-insertion order (the
 * reported "shows 1 person randomly" bug), and never an id that has since left the call.
 *
 * Priority:
 *   1. the live active speaker (`isActiveSpeaker`), else the most-recent one
 *      (`lastActiveSpeaker`) — the promoted "last-known speaker". Live wins over stale even
 *      when the stale entry sits earlier in the list.
 *   2. otherwise keep the previous pick IFF it is still connected (avoids needless churn);
 *   3. otherwise a stable, order-independent pick: the lowest `connectionId`.
 * Returns '' when there are no remotes.
 */
export const resolveActiveConnectionId = (connections: TRTCConnection[], prevId: string): string => {
  if (connections.length === 0) return '';
  const speaker =
    connections.find((remote) => remote.isActiveSpeaker) ?? connections.find((remote) => remote.lastActiveSpeaker);
  if (speaker) return speaker.connectionId;
  if (prevId && connections.some((remote) => remote.connectionId === prevId)) return prevId;
  return connections.reduce((lowest, remote) => (remote.connectionId < lowest.connectionId ? remote : lowest))
    .connectionId;
};
